import React, { useState, useEffect, useRef } from 'react';
import { FaMicrophone, FaTimes, FaSpinner, FaMinus, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import '../styles/LyricsDisplay.css';

const API = process.env.REACT_APP_API_URL || '';

function LyricsDisplay({ trackId, currentTime, onClose }) {
  const [lyrics, setLyrics] = useState(null);
  const [lines, setLines] = useState([]);
  const [activeLine, setActiveLine] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const containerRef = useRef();

  useEffect(() => {
    if (trackId) { searchLyrics(); setOffset(0); }
    else { setLyrics(null); setLines([]); setError(null); }
  }, [trackId]);

  useEffect(() => {
    if (!lyrics?.is_synced || !lines.length) return;
    const adjusted = currentTime + offset;
    const idx = lines.reduce((best, line, i) => {
      if (line.time <= adjusted) return i;
      return best;
    }, -1);
    if (idx !== activeLine) {
      setActiveLine(idx);
      if (containerRef.current && idx >= 0) {
        const el = containerRef.current.children[idx];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentTime, lines, offset]);

  const searchLyrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/lyrics/search/${trackId}`);
      setLyrics(res.data);
      if (res.data.is_synced) {
        parseLRC(res.data.content);
      } else {
        setLines(res.data.content.split('\n').map(text => ({ time: 0, text })));
      }
    } catch (e) {
      setError('Aucune parole trouvée pour cette piste');
      setLyrics(null);
      setLines([]);
    } finally {
      setLoading(false);
    }
  };

  const parseLRC = (content) => {
    const parsed = [];
    const regex = /\[(\d{2}):(\d{2})\.(\d{2,3})\](.*)/;
    content.split('\n').forEach(line => {
      const match = line.match(regex);
      if (match) {
        const time = parseInt(match[1]) * 60 + parseInt(match[2]) + parseInt(match[3]) / (match[3].length === 3 ? 1000 : 100);
        parsed.push({ time, text: match[4].trim() });
      }
    });
    setLines(parsed.sort((a, b) => a.time - b.time));
  };

  return (
    <div className="lyrics-panel">
      <div className="lyrics-header">
        <h3><FaMicrophone /> Paroles</h3>
        <button onClick={onClose} className="lyrics-close"><FaTimes /></button>
      </div>
      {lyrics?.is_synced && (
        <div className="lyrics-offset">
          <button onClick={() => setOffset(o => o - 0.5)}>-0.5s</button>
          <span>{offset > 0 ? '+' : ''}{offset.toFixed(1)}s</span>
          <button onClick={() => setOffset(o => o + 0.5)}>+0.5s</button>
        </div>
      )}
      <div className="lyrics-content" ref={containerRef}>
        {loading && <p className="lyrics-loading"><FaSpinner className="spin" /> Recherche des paroles...</p>}
        {error && <p className="lyrics-error">{error}</p>}
        {!loading && !error && lines.map((line, i) => (
          <p key={i} className={`lyrics-line ${lyrics?.is_synced && i === activeLine ? 'active' : ''} ${lyrics?.is_synced && i < activeLine ? 'past' : ''}`}>
            {line.text || '♪'}
          </p>
        ))}
      </div>
      {lyrics?.source && <div className="lyrics-source">Source: {lyrics.source.startsWith('lrclib') ? 'LRCLIB (auto)' : lyrics.source}</div>}
    </div>
  );
}

export default LyricsDisplay;
